import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@repo/utils";
import type { DialogBaseProps } from "../dialog.types.js";
import { useDialogContext } from "../use-dialog-context.js";

type Props = DialogBaseProps & {
  onClose?: () => void;
  disableXClose?: boolean;
  allowNavigation?: boolean;
};

export const DialogContent = ({
  children,
  className,
  onClose,
  disableXClose,
  allowNavigation,
}: Props) => {
  const { open, setOpen } = useDialogContext();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // Body scroll lock
    document.body.style.overflow = "hidden";

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open, setOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!open || !contentRef.current) return;

    const focusableElements = contentRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);
    firstElement?.focus();

    return () => {
      document.removeEventListener("keydown", handleTab);
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-allow-navigation={allowNavigation || undefined}
    >
      <div
        data-dialog-backdrop
        {...(disableXClose ? { "data-dialog-no-close": "true" } : {})}
        className="animate-fade-in fixed inset-0 bg-black/50"
        onClick={() => {
          if (disableXClose) return;
          setOpen(false);
          onClose?.();
        }}
      />
      <div
        ref={contentRef}
        className={cn(
          "animate-dialog-in relative z-50 w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg",
          "max-h-[90vh]",
          "overflow-y-auto",
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        {!disableXClose && (
          <button
            onClick={() => {
              setOpen(false);
              onClose?.();
            }}
            className="absolute top-4 right-4 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};
